import { error } from 'utils/console';
import getSplashApiClient from 'lib/SplashApiClient/singleton';
import * as actionCreators from './actionCreators';

const ERR_MSG_DELETE_PROCESS = 'Could not delete the process! Please refresh this page.';
const ERR_MSG_DISMISS_PROCESS = 'Could not dismiss the process! Please refresh this page.';
const ERR_MSG_LOAD_PROCESSES = 'Could not load the processes! Please refresh this page.';

const CONSOLE_LOG_KEY = 'processTracker';

/**
 * Helper method to retrieve the event processes API URL.
 *
 * @param {integer} eventId
 * @return {string} apiUrl
 */
export const getUserProcessesForEventUrl = (eventId) => `/user/processes/event/${eventId}`;

export const getUserProcesses = () => '/user/processes';

/**
 * Helper method to retrieve the dismiss a process API URL.
 *
 * @param {integer} processId
 * @return {string} apiUrl
 */
export const getDismissProcessUrl = (processId) => `/user/processes/${processId}/dismiss`;

/**
 * Helper method to retrieve the delete a process API URL.
 *
 * @param {integer} processId
 * @return {string} apiUrl
 */
export const getDeleteProcessUrl = (processId) => `/user/processes/${processId}`;

/**
 * Sets the interval at which the ProcessTracker will poll for updated data.
 *
 * @param  {integer} newPollInterval
 * This is the interval at which the process trackers will be requested. This
 * value is in milliseconds.
 */
let pollRate = 10000;
export const setPollRate = (newPollRate) => (pollRate = newPollRate);

/**
 * @var {number} pollTimeout
 * timeout loop identifier for fetching updates. This is updated
 * by the `startPollingLoop` method when polling the API endpoint.
 * We keep track of this so that we can clear (stop) the timeout when needed.
 */
let pollTimeout;

/**
 * @var {boolean} keepPolling
 * flag that defines if we should keep pulling for more process trackers or not
 */
let keepPolling = false;

/**
 * Keep track of the current eventId so it can be reused in a few events.
 */
let eventId;
export const setEventId = (id) => (eventId = id);

/**
 * ProcessTracker Actions
 * ======================
 *
 * This should be abstracted into the ProcessTracker component once we determine what
 * patterns we want to follow for complex components with their own actions.
 */
const apiLoadProcesses = () =>
    eventId
        ? getSplashApiClient().get(getUserProcessesForEventUrl(eventId))
        : getSplashApiClient().get(getUserProcesses());

const apiDismissProcess = (processId, processEventId) =>
    getSplashApiClient().put(getDismissProcessUrl(processId), {
        eventId: processEventId || eventId,
    });
const apiDeleteProcess = (processId, processEventId) =>
    getSplashApiClient().delete(getDeleteProcessUrl(processId), {
        eventId: processEventId || eventId,
    });

/**
 * Stops the ongoing process tracker API poll timeout if one exists.
 */
function stopPolling() {
    pollTimeout && clearTimeout(pollTimeout);
    keepPolling = false;
}

/**
 * Handles a non-200 OK response from the API. Stops polling whenever this occurs.
 *
 * @param {function} dispatch
 * @param {object} resp
 * @param {string} errorMessage
 */
const handleApiErrorResponse = (dispatch, resp, errorMessage) => {
    error(CONSOLE_LOG_KEY, resp);

    stopPolling();
    dispatch(actionCreators.stoppedPollingProcesses());

    dispatch(
        actionCreators.displayError(errorMessage, () => {
            dispatch(actionCreators.dismissAlert());
        }),
    );
};

/**
 * Stops polling the API endpoint for the processes.
 */
export const stopPollingProcesses = () => (dispatch) => {
    stopPolling();
    dispatch(actionCreators.stoppedPollingProcesses());
};

/**
 * Sends a request to the API endpoint for retrieving processes that apply
 * to the logged in user for this event.
 *
 * @param  {integer} eventId
 * @return {Promise}
 */
export const loadProcesses = () => (dispatch) => {
    dispatch(actionCreators.loadingProcesses());

    return apiLoadProcesses(eventId)
        .then((response) => {
            if (response.data && response.data.length === 0) {
                dispatch(stopPollingProcesses());
            }

            if (!eventId) {
                // Converts { eventId1: [process1, proccess2], eventId2: [process3, process4]}
                // to
                // [[process1, process2], [process3, process4]]
                // and adds the eventId as a property to each
                const processesByEvent = Object.entries(response.data).map(([processEventId, processList]) =>
                    processList.map((process) => ({
                        ...process,
                        eventId: processEventId,
                    })),
                );
                // Converts [[process1, process2], [process3, process4]]
                // to
                // [process1, process2, process3, process4]
                const combinedProcesses = processesByEvent.reduce((combined, current) => [...combined, ...current], []);

                if (!combinedProcesses.length) {
                    dispatch(stopPollingProcesses());
                }

                dispatch(
                    actionCreators.loadedProcesses({
                        data: combinedProcesses,
                    }),
                );
            } else {
                dispatch(actionCreators.loadedProcesses(response));
            }
        })
        .catch((response) => handleApiErrorResponse(dispatch, response, ERR_MSG_LOAD_PROCESSES));
};

/**
 * Start polling in a loop of timeouts
 * Not using setInterval because we want the request finish to start timing the pollRate
 * @param dispatch
 */
const startPollingLoop = (dispatch) => {
    const delayedAction = () => {
        if (keepPolling) {
            pollTimeout = setTimeout(() => startPollingLoop(dispatch), pollRate);
        }
    };
    loadProcesses()(dispatch).then(delayedAction);
};

/**
 * Polls the API endpoint that returns the processes that apply to the user. Polls
 * at a rate of `pollRate` ms. The `setPollRate` method can be used to
 * change the rate at which requests are made.
 */
export const startPollingProcesses = () => (dispatch) => {
    // Clear any pre-existing pooling loop before starting a new one.
    stopPolling();
    keepPolling = true;
    startPollingLoop(dispatch);
    dispatch(actionCreators.startedPollingProcesses());
};

/**
 * Sends an API request to delete the process which means that the process
 * will never appear again. This is a permanent dismiss.
 *
 * @param  {integer} processId
 */
export const deleteProcess = (processId) => (dispatch, getState) => {
    // Check to see if the process is completed. If it is, we need to send
    // that information in the dispatches.
    const process = getState().entities.processes[processId];
    const isCompleted = !!process.completedAt;

    dispatch(actionCreators.deletingProcess(processId, isCompleted));

    return apiDeleteProcess(processId, process.eventId)
        .then((response) => {
            dispatch(actionCreators.deletedProcess(processId, isCompleted));
        })
        .catch((response) => handleApiErrorResponse(dispatch, response, ERR_MSG_DELETE_PROCESS));
};

/**
 * Sends an API request to dismiss the process which means the user no longer
 * wishes to track it. Once a process completes, it becomes undismissed to tell
 * the user that it was completed.
 *
 * If a completed process is dismissed, we should defer to the `deleteProcess`
 * API endpoint. We never wish to see it again.
 *
 * @param  {integer} processId
 */
export const dismissProcess = (processId) => (dispatch, getState) => {
    // Check to see if this process is completed. If so, invoke the
    // `deleteProcess` action instead.
    const process = getState().entities.processes[processId];
    if (process && process.completedAt) {
        return deleteProcess(processId)(dispatch);
    }

    dispatch(actionCreators.dismissingProcess(processId));
    return apiDismissProcess(processId, process.eventId)
        .then((response) => {
            dispatch(actionCreators.dismissedProcess(processId));
        })
        .catch((response) => handleApiErrorResponse(dispatch, response, ERR_MSG_DISMISS_PROCESS));
};

export const minimizeProcessTracker = () => actionCreators.minimizeProcessTracker();

export const maximizeProcessTracker = () => actionCreators.maximizeProcessTracker();

/**
 * Refresh the onCompletedActions in the processes.
 * This action is not handle by any reducer as it doesn't change the state.
 * The properties are handle in the `mapStateToProps`
 * function in the ProcessTracker Component
 */
export const refreshOnCompletedButton = () => actionCreators.refreshOnCompletedButtons();
